/**
 * Alpha Workwear signup - shared form behaviour, and the offer modal.
 *
 * TWO JOBS, ONE FILE, on purpose. The inline newsletter band and the modal are
 * the same ask rendered twice, and the validation below has to behave identically
 * in both or the visitor gets two different answers to the same typo. The band
 * loads this file for the validation half and never touches the modal half; the
 * modal snippet loads it for both. Every binding is idempotent, so a page that
 * renders both and therefore includes the file twice is harmless.
 *
 * ---- the modal --------------------------------------------------------------
 *
 * Opens on whichever comes first: a merchant-set delay, or exit intent. Then it
 * is done - a dismissal is written to localStorage and honoured for a merchant-set
 * number of days, and a successful signup silences it permanently in that browser.
 *
 * The rules that matter more than the effect:
 *   - It is genuinely dismissible. Close button, Escape, backdrop click, and a
 *     plain-language decline link, all of which persist the dismissal.
 *   - Focus is trapped while it is open and returned to whatever had it before.
 *   - It never appears on cart or checkout paths, and never to someone who has
 *     already subscribed here.
 *   - It never lands on top of the cookie-consent banner. First load is the one
 *     load where both want the screen, and two stacked dialogs is how a visitor
 *     ends up dismissing consent by accident - see isBlocked() below.
 *   - localStorage throws in some privacy modes, so every access is guarded and
 *     falls back to "show at most once this page load".
 *
 * The customer form posts the whole page rather than using AJAX, so a submission
 * is a navigation. A sessionStorage marker set on submit is what lets the popup
 * know, on the way back in, that the reply belongs to it and not to the inline
 * newsletter section further down the page.
 */

(function () {
  const DISMISSED_KEY = 'alpha:offer:dismissed';
  const SUBSCRIBED_KEY = 'alpha:offer:subscribed';
  const PENDING_KEY = 'alpha:offer:pending';
  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  // Cart, checkout, order and account paths, with or without a locale prefix
  // (/en-ie/cart). Liquid already excludes the cart page; this covers app proxy
  // and localised routes the section render never sees.
  const BLOCKED_PATH = /^(?:\/[a-z]{2}(?:-[a-z]{2})?)?\/(cart|checkout|orders|account)(\/|$)/i;

  // Shopify's own consent banner, then the two markup shapes the common consent
  // apps use. Matching by id/class rather than by asking the Customer Privacy
  // API keeps this working when consent is served by an app instead of Shopify:
  // if any of them is on screen and has a box, something is asking the visitor a
  // question already.
  const CONSENT_BANNER =
    '#shopify-pc__banner, .shopify-pc__banner, #shopify-pc__prefs, [data-consent-banner], .cookie-banner';

  // How long to keep waiting for a consent banner or an open drawer to clear,
  // and how often to look. 20 x 3s is a generous minute: long enough for someone
  // to read a consent notice and decide, short enough that the popup gives up
  // and stays away rather than ambushing them ten minutes into a session.
  const RETRY_MS = 3000;
  const MAX_DEFERRALS = 20;

  // Deliberately permissive. The server is the authority on whether an address
  // exists; this only has to catch the two mistakes that are worth a page load -
  // an empty field, and something with no @ or no dot after it.
  const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  const store = {
    get: function (key) {
      try {
        return window.localStorage.getItem(key);
      } catch (e) {
        return null;
      }
    },
    set: function (key, value) {
      try {
        window.localStorage.setItem(key, value);
      } catch (e) {
        /* Private mode, quota, or storage disabled - the popup still behaves,
           it just forgets between page loads. */
      }
    },
  };

  const session = {
    get: function (key) {
      try {
        return window.sessionStorage.getItem(key);
      } catch (e) {
        return null;
      }
    },
    set: function (key, value) {
      try {
        window.sessionStorage.setItem(key, value);
      } catch (e) {}
    },
    remove: function (key) {
      try {
        window.sessionStorage.removeItem(key);
      } catch (e) {}
    },
  };

  /**
   * Client-side validation for any .alpha-subscribe form - the band and the modal.
   *
   * The customer form posts the whole page, so an address with a missing dot
   * costs a full round trip and comes back with the visitor scrolled somewhere
   * else. Catching it here turns that into an inline message and a focused field.
   *
   * The browser's own bubble is switched off ONLY once this has run: without
   * scripting, `required` and `type="email"` still do the work natively, so the
   * field is never left unvalidated by a script that failed to load.
   */
  function bindValidation(form) {
    if (!form || form.dataset.alphaSubscribeReady === 'true') return;

    const input = form.querySelector('[data-alpha-subscribe-input]');
    const message = form.querySelector('[data-alpha-subscribe-error]');
    // No control on this render - the success state has replaced it.
    if (!input || !message) return;

    form.dataset.alphaSubscribeReady = 'true';
    form.noValidate = true;

    const text = message.querySelector('[data-alpha-subscribe-error-text]');
    const wording = message.dataset.message;

    function show() {
      if (text && wording) text.textContent = wording;
      message.hidden = false;
      input.setAttribute('aria-invalid', 'true');
      if (message.id) input.setAttribute('aria-describedby', message.id);
    }

    function clear() {
      message.hidden = true;
      input.removeAttribute('aria-invalid');
      input.removeAttribute('aria-describedby');
    }

    form.addEventListener('submit', function (event) {
      if (EMAIL.test(input.value.trim())) return;
      event.preventDefault();
      show();
      input.focus();
    });

    // Only while an error is on screen, so the message does not flicker away on
    // the first keystroke of a field that was never wrong.
    input.addEventListener('input', function () {
      if (input.getAttribute('aria-invalid') === 'true') clear();
    });
  }

  function init(root) {
    if (!root || root.dataset.alphaOfferReady === 'true') return;
    root.dataset.alphaOfferReady = 'true';

    const dialog = root.querySelector('[data-alpha-offer-dialog]');
    if (!dialog) return;

    const designMode = root.dataset.designMode === 'true';
    const delay = parseInt(root.dataset.delay, 10) || 0;
    // DAYS. Multiplied by 86400000 - one day in milliseconds - in suppressed()
    // below, so the merchant's "2" is exactly 48 hours. The fallback matches the
    // theme setting's default rather than being a second opinion about it.
    const days = parseInt(root.dataset.days, 10) || 2;
    const exitIntentEnabled = root.dataset.exitIntent !== 'false';

    const success = root.querySelector('[data-alpha-offer-success]');
    const input = root.querySelector('[data-alpha-offer-input]');
    const form = root.querySelector('form');

    // The error element is now always in the DOM so the validation above has
    // somewhere to write. Only a SERVER error arrives already visible, and only
    // a server error means "re-open this dialog and let them fix it".
    const errorEl = root.querySelector('[data-alpha-offer-error]');
    const failure = errorEl && !errorEl.hidden ? errorEl : null;

    // The reply to our own submit, or to some other customer form on the page.
    const pending = session.get(PENDING_KEY) === '1';
    session.remove(PENDING_KEY);

    // Not in the editor: a merchant who tests the form once would otherwise
    // silence the popup in their own browser forever and conclude it is broken.
    if (success && !designMode) store.set(SUBSCRIBED_KEY, String(Date.now()));

    let isOpen = false;
    let timer = null;
    let lastFocused = null;
    let deferrals = 0;

    // Two reasons not to open right now, and the same answer to both: wait.
    //
    // 1. Dawn locks the body with `overflow-hidden` while the cart drawer, menu
    //    drawer or a modal is open. Dropping this on top of one of those would
    //    be two dialogs deep.
    // 2. The cookie-consent banner. This is the one that actually bites: consent
    //    appears on first load, and first load is exactly when a delay-triggered
    //    signup modal fires. Covering a consent notice with a marketing dialog
    //    is bad manners and worse compliance - the visitor cannot answer the
    //    question they are being asked, and the nearest button belongs to us.
    //
    // Checked at open() time rather than once at boot, because the banner is
    // injected by content_for_header after this script has already run.
    function isBlocked() {
      if (/(^|\s)overflow-hidden/.test(document.body.className)) return true;

      // querySelectorAll, not querySelector: an answered banner is usually left
      // in the DOM with display:none, and if a hidden one happens to sit earlier
      // in document order than the live one, taking only the first match would
      // report "nothing on screen" while the real notice is still up. Presence
      // is not the test - having a box is.
      return Array.prototype.some.call(document.querySelectorAll(CONSENT_BANNER), function (el) {
        return el.offsetWidth > 0 || el.offsetHeight > 0;
      });
    }

    function suppressed() {
      if (BLOCKED_PATH.test(window.location.pathname)) return true;
      // The merchant is editing it - a remembered dismissal, or a signup they
      // made while testing, would make the thing they are working on
      // impossible to look at. Checked before both markers, not between them.
      if (designMode) return false;
      if (store.get(SUBSCRIBED_KEY)) return true;

      const dismissedAt = parseInt(store.get(DISMISSED_KEY), 10);
      if (!dismissedAt) return false;
      // 86400000 = 24 * 60 * 60 * 1000, one day. days = 2 is therefore a
      // 48-hour window, which is what the merchant asked for.
      return Date.now() - dismissedAt < days * 86400000;
    }

    function focusables() {
      return Array.prototype.filter.call(dialog.querySelectorAll(FOCUSABLE), function (el) {
        return el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement;
      });
    }

    function onKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        close();
        return;
      }
      if (event.key !== 'Tab') return;

      const items = focusables();
      if (items.length === 0) {
        event.preventDefault();
        return;
      }

      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;

      // Both branches also catch focus having escaped the dialog entirely, e.g.
      // after the browser moved it to the address bar and back. Guarding only
      // the shift branch would leak on the very next forward Tab.
      const escaped = !dialog.contains(active);

      if (event.shiftKey && (active === first || escaped)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || escaped)) {
        event.preventDefault();
        first.focus();
      }
    }

    /**
     * @param {Element|null} [focusTarget] where to put focus once it is up
     * @param {boolean} [force] the visitor asked for it - a click on a
     *   [data-alpha-offer-open] trigger. An explicit request outranks both the
     *   deferral rules and the dismissal window; nothing is being interrupted
     *   when the interruption is the thing that was clicked.
     */
    function open(focusTarget, force) {
      if (isOpen) return;

      clearTimeout(timer);

      if (!force && isBlocked()) {
        // Give up rather than force it. The old behaviour opened anyway once
        // the retries ran out, which is the exact failure this guards against:
        // a dialog stacked on a consent banner that never went away.
        if (deferrals >= MAX_DEFERRALS) return;
        deferrals += 1;
        timer = setTimeout(function () {
          open(focusTarget);
        }, RETRY_MS);
        return;
      }

      document.removeEventListener('mouseout', onExitIntent);

      isOpen = true;
      lastFocused = document.activeElement;
      root.classList.add('is-open');
      document.body.classList.add('alpha-offer-lock');
      document.addEventListener('keydown', onKeydown, true);

      const target = focusTarget || root.querySelector('[data-alpha-offer-close]') || dialog;
      if (target === dialog) dialog.setAttribute('tabindex', '-1');
      target.focus();
    }

    function close() {
      if (!isOpen) return;
      isOpen = false;
      root.classList.remove('is-open');
      document.body.classList.remove('alpha-offer-lock');
      document.removeEventListener('keydown', onKeydown, true);
      store.set(DISMISSED_KEY, String(Date.now()));

      if (lastFocused && document.contains(lastFocused)) lastFocused.focus();
      lastFocused = null;
    }

    function onExitIntent(event) {
      // A real exit is the cursor crossing the top edge of the document with no
      // element underneath it; relatedTarget is null only when it leaves the page.
      if (event.clientY > 0 || event.relatedTarget) return;
      open();
    }

    // ---- dismissal controls -------------------------------------------------

    root.querySelectorAll('[data-alpha-offer-close]').forEach(function (button) {
      button.addEventListener('click', close);
    });

    root.addEventListener('mousedown', function (event) {
      // The root IS the backdrop; a press that starts on the dialog and drags
      // out (selecting text) must not count as a backdrop click, which is why
      // this is mousedown on the target rather than a click anywhere.
      if (event.target === root) close();
    });

    // ---- manual triggers ----------------------------------------------------

    // Anything, anywhere, carrying data-alpha-offer-open - a footer link, a
    // menu item. This is the only way back in once someone has dismissed it, and
    // it is bound before the suppressed() return below so it works on every page
    // load, not just the ones where the dialog was going to appear anyway.
    document.querySelectorAll('[data-alpha-offer-open]').forEach(function (trigger) {
      if (trigger.dataset.alphaOfferBound === 'true') return;
      trigger.dataset.alphaOfferBound = 'true';
      trigger.addEventListener('click', function (event) {
        event.preventDefault();
        open(input || null, true);
      });
    });

    // ---- discount code copy -------------------------------------------------

    const copyButton = root.querySelector('[data-alpha-offer-copy]');
    if (copyButton) {
      copyButton.addEventListener('click', function () {
        const code = root.querySelector('[data-alpha-offer-code]');
        const label = copyButton.querySelector('[data-alpha-offer-copy-text]');
        if (!code || !label) return;

        const done = function () {
          const previous = label.textContent;
          label.textContent = copyButton.dataset.copiedLabel || previous;
          copyButton.classList.add('is-copied');
          setTimeout(function () {
            label.textContent = previous;
            copyButton.classList.remove('is-copied');
          }, 2000);
        };

        if (navigator.clipboard && window.isSecureContext) {
          navigator.clipboard.writeText(code.textContent.trim()).then(done, selectCode);
        } else {
          selectCode();
        }

        // No clipboard API: put the code under the user's selection so one
        // keystroke copies it. Better than a button that appears to do nothing.
        function selectCode() {
          const range = document.createRange();
          range.selectNodeContents(code);
          const selection = window.getSelection();
          selection.removeAllRanges();
          selection.addRange(range);
        }
      });
    }

    // ---- submit -------------------------------------------------------------

    if (form) {
      form.addEventListener('submit', function (event) {
        // bindValidation may have stopped this submit. Marking it pending anyway
        // would make the NEXT page load think a reply is owed to this dialog.
        if (event.defaultPrevented) return;
        session.set(PENDING_KEY, '1');
      });
    }

    // ---- what to do on this page load --------------------------------------

    if (pending && success) {
      // Their own signup came back - show them it worked, then never again.
      open(root.querySelector('[data-alpha-offer-close]'));
      return;
    }

    if (pending && failure) {
      open(input || null);
      return;
    }

    if (suppressed()) return;

    if (delay > 0) {
      timer = setTimeout(function () {
        open();
      }, delay);
    }

    if (exitIntentEnabled && !designMode && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      document.addEventListener('mouseout', onExitIntent);
    }
  }

  function boot() {
    // Forms first. init() adds its own submit listener, and listeners fire in
    // registration order - the validation has to get there first for the
    // defaultPrevented guard in that listener to mean anything.
    document.querySelectorAll('form.alpha-subscribe').forEach(bindValidation);
    document.querySelectorAll('[data-alpha-offer]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // The theme editor re-renders the layout when the merchant changes a setting.
  document.addEventListener('shopify:section:load', boot);
})();
